import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { BSMARTR_KLEUREN, contrast, controleerHuisstijl, huisstijlVariabelen, isHex } from '@ep/core';
import { api } from '../lib/api';
import { useIk } from '../lib/data';
import { toast } from '../ui/toast';
import { Laden, Veld } from '../ui/ui';

const MAX = 300 * 1024;
const VOORBEELDEN: [string, string, string][] = [
  ['bsmartr', BSMARTR_KLEUREN.primair, BSMARTR_KLEUREN.accent],
  ['Zorggroen', '#00563F', '#8CC63F'],
  ['Ziekenhuisblauw', '#00467F', '#00A3E0'],
  ['Paars', '#4B2E83', '#E5007D'],
  ['Antraciet', '#2F3A45', '#F2A900'],
  ['Bordeaux', '#7A1E3A', '#F28C28'],
];

/** Verkleint een foto tot max 800×300 en geeft een PNG-data-URL; SVG blijft zoals hij is. */
async function leesBestand(f: File): Promise<string> {
  const dataUrl = await new Promise<string>((ok, fout) => {
    const r = new FileReader();
    r.onload = () => ok(String(r.result));
    r.onerror = () => fout(new Error('Bestand kon niet worden gelezen'));
    r.readAsDataURL(f);
  });
  if (f.type === 'image/svg+xml') {
    if (f.size > MAX) throw new Error('Het SVG-bestand is te groot (maximaal 300 kB)');
    return dataUrl;
  }
  if (!['image/png', 'image/jpeg', 'image/webp'].includes(f.type)) throw new Error('Kies een PNG, JPG, WebP of SVG');
  const img = await new Promise<HTMLImageElement>((ok, fout) => {
    const i = new Image();
    i.onload = () => ok(i);
    i.onerror = () => fout(new Error('Dit plaatje kon niet worden geopend'));
    i.src = dataUrl;
  });
  const schaal = Math.min(1, 800 / img.width, 300 / img.height);
  const c = document.createElement('canvas');
  c.width = Math.round(img.width * schaal);
  c.height = Math.round(img.height * schaal);
  c.getContext('2d')!.drawImage(img, 0, 0, c.width, c.height);
  const uit = c.toDataURL('image/png');
  if ((uit.length * 3) / 4 > MAX) throw new Error('Het logo is ook na verkleinen te groot (maximaal 300 kB)');
  return uit;
}

function KleurVeld({ label, waarde, onChange, hint }: { label: string; waarde: string; onChange: (k: string) => void; hint?: string }) {
  const [tekst, setTekst] = useState(waarde);
  useEffect(() => setTekst(waarde), [waarde]);
  return (
    <Veld label={label} hint={hint}>
      <div className="rij" style={{ gap: 8 }}>
        <input type="color" value={isHex(waarde) ? waarde : '#000000'} onChange={(e) => onChange(e.target.value.toUpperCase())} style={{ width: 46, height: 36, padding: 2, cursor: 'pointer' }} />
        <input value={tekst} maxLength={7} onChange={(e) => { setTekst(e.target.value); if (isHex(e.target.value)) onChange(e.target.value.toUpperCase()); }} style={{ width: 110, fontFamily: 'monospace' }} />
      </div>
    </Veld>
  );
}

/** SPEC §13.1: Beheer › Instellingen › Huisstijl. */
export function HuisstijlScherm() {
  const ik = useIk().data;
  const qc = useQueryClient();
  const hs = ik?.organisatie.huisstijl;
  const [primair, setPrimair] = useState<string>(hs?.primair ?? BSMARTR_KLEUREN.primair);
  const [accent, setAccent] = useState<string>(hs?.accent ?? BSMARTR_KLEUREN.accent);
  /** undefined = ongewijzigd, null = weghalen, string = nieuw */
  const [logo, setLogo] = useState<string | null | undefined>(undefined);
  const [bezig, setBezig] = useState(false);
  if (!ik) return <Laden />;

  const huidigLogo = logo === undefined ? (hs?.logoVersie ? `/api/huisstijl/logo?v=${hs.logoVersie}` : null) : logo;
  const c = controleerHuisstijl({ primair, accent });
  const v = huisstijlVariabelen({ primair, accent });
  const opslaan = async (reset = false) => {
    setBezig(true);
    try {
      const body = reset ? { primair: null, accent: null, logo: null } : { primair: primair === BSMARTR_KLEUREN.primair ? null : primair, accent: accent === BSMARTR_KLEUREN.accent ? null : accent, ...(logo !== undefined ? { logo } : {}) };
      const r = await api.put<{ waarschuwing: string | null }>('/api/organisatie/huisstijl', body);
      await qc.invalidateQueries({ queryKey: ['ik'] });
      if (reset) {
        setPrimair(BSMARTR_KLEUREN.primair);
        setAccent(BSMARTR_KLEUREN.accent);
      }
      setLogo(undefined);
      toast(r.waarschuwing ?? (reset ? 'Standaard bsmartr-huisstijl teruggezet' : 'Huisstijl opgeslagen'), r.waarschuwing ? 'info' : 'succes');
    } catch (e) {
      toast((e as Error).message, 'fout');
    } finally {
      setBezig(false);
    }
  };
  const kies = async (f?: File) => {
    if (!f) return;
    try {
      setLogo(await leesBestand(f));
    } catch (e) {
      toast((e as Error).message, 'fout');
    }
  };

  return (
    <div className="pagina kolom" style={{ gap: 16, maxWidth: 1100 }}>
      <div className="rij">
        <div className="groei">
          <h1>Huisstijl</h1>
          <p className="mute" style={{ margin: '4px 0 0' }}>Laat bsmartr zien in de kleuren en met het logo van {ik.organisatie.naam}. Iedereen in jullie organisatie ziet dit.</p>
        </div>
        <button className="knop" disabled={bezig} onClick={() => opslaan(true)}>Standaard bsmartr</button>
        <button className="knop primair" disabled={bezig || !!c.fout} onClick={() => opslaan()}>Opslaan</button>
      </div>

      <div className="grid2" style={{ alignItems: 'start' }}>
        <div className="paneel kolom" style={{ gap: 14 }}>
          <h2>Kleuren</h2>
          <div className="rij wrap" style={{ gap: 18 }}>
            <KleurVeld label="Hoofdkleur" waarde={primair} onChange={setPrimair} hint="Bovenbalk, knoppen en koppen" />
            <KleurVeld label="Accentkleur" waarde={accent} onChange={setAccent} hint="Actieve tab, lijnen en accenten" />
          </div>
          {c.fout && <div className="klein" style={{ color: 'var(--rood)' }}>{c.fout}</div>}
          {c.waarschuwing && <div className="klein" style={{ color: 'var(--oranje)' }}>{c.waarschuwing}</div>}
          {!c.fout && <div className="klein mute">Contrast witte tekst op de hoofdkleur: {contrast(primair, '#FFFFFF')}:1 (minimaal 4,5:1).</div>}
          <div>
            <div className="klein mute" style={{ marginBottom: 6 }}>Voorbeelden</div>
            <div className="rij wrap" style={{ gap: 8 }}>
              {VOORBEELDEN.map(([n, p, a]) => (
                <button key={n} className="knop klein" onClick={() => { setPrimair(p); setAccent(a); }} title={`${p} / ${a}`}>
                  <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 3, background: p, marginRight: 2 }} />
                  <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 3, background: a, marginRight: 6 }} />
                  {n}
                </button>
              ))}
            </div>
          </div>

          <h2 style={{ marginTop: 8 }}>Logo</h2>
          <div className="rij wrap" style={{ gap: 12, alignItems: 'center' }}>
            <div style={{ width: 220, height: 80, border: '1px dashed var(--lijn)', borderRadius: 8, display: 'grid', placeItems: 'center', background: '#fff' }}>
              {huidigLogo ? <img src={huidigLogo} alt="" style={{ maxWidth: 200, maxHeight: 64, objectFit: 'contain' }} /> : <span className="klein mute">Geen eigen logo</span>}
            </div>
            <div className="kolom" style={{ gap: 6 }}>
              <label className="knop" style={{ cursor: 'pointer' }}>
                Logo kiezen…
                <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" style={{ display: 'none' }} onChange={(e) => kies(e.target.files?.[0])} />
              </label>
              {huidigLogo && <button className="knop klein" onClick={() => setLogo(null)}>Logo weghalen</button>}
            </div>
          </div>
          <div className="klein mute">PNG, JPG, WebP of SVG. Liefst een liggend logo op een transparante of witte achtergrond. Grote foto's worden automatisch verkleind (max. 300 kB).</div>
        </div>

        <div className="paneel kolom" style={{ gap: 12 }}>
          <h2>Voorbeeld</h2>
          <div style={{ borderRadius: 10, overflow: 'hidden', border: '1px solid var(--lijn-2)', display: 'grid', gridTemplateColumns: '150px 1fr', minHeight: 250 }}>
            <div style={{ background: v['--accent'], color: '#fff', padding: 12, display: 'flex', flexDirection: 'column', gap: 6, borderRight: `3px solid ${v['--merk']}` }}>
              {huidigLogo ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginBottom: 6 }}>
                  <span style={{ background: '#fff', borderRadius: 6, padding: '4px 6px', height: 34, display: 'flex', alignItems: 'center' }}><img src={huidigLogo} alt="" style={{ height: 26, maxWidth: 120, objectFit: 'contain' }} /></span>
                  <small style={{ fontSize: 9, opacity: 0.8 }}>powered by <b>bsmart<i style={{ color: v['--merk'] }}>r</i></b></small>
                </div>
              ) : (
                <img src="/logo-wit.svg" alt="bsmartr" style={{ height: 18, marginBottom: 8, alignSelf: 'flex-start' }} />
              )}
              {['Start', 'Projecten', 'Taken', 'Weekrooster'].map((n, i) => (
                <span key={n} style={{ fontSize: 12, padding: '5px 8px', borderRadius: 6, ...(i === 1 ? { background: '#fff', color: v['--accent'], fontWeight: 700, boxShadow: `inset 3px 0 0 ${v['--merk']}` } : { opacity: 0.85 }) }}>{n}</span>
              ))}
            </div>
            <div style={{ background: 'var(--bg)', display: 'flex', flexDirection: 'column' }}>
              <div style={{ background: '#fff', borderBottom: '1px solid var(--lijn-2)', padding: '9px 12px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="mute">Projecten ›</span> <b>204295</b>
                <span style={{ marginLeft: 'auto', width: 24, height: 24, borderRadius: '50%', background: v['--accent'], color: '#fff', display: 'grid', placeItems: 'center', fontSize: 10, fontWeight: 700 }}>BB</span>
              </div>
              <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: v['--accent'] }}>Implementatie patiëntportaal</div>
                <div className="rij" style={{ gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ background: v['--accent'], color: '#fff', borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 600 }}>Rapport</span>
                  <span style={{ background: v['--merk'], color: '#fff', borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 600 }}>Timer</span>
                  <span style={{ background: v['--accent-bg'], color: v['--accent'], borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 700 }}>Bezig</span>
                </div>
                <div style={{ height: 7, borderRadius: 4, background: '#dde3ef', display: 'flex', overflow: 'hidden' }}>
                  <i style={{ width: '35%', background: v['--accent'] }} />
                  <i style={{ width: '20%', background: v['--merk'] }} />
                </div>
              </div>
            </div>
          </div>
          <p className="klein mute" style={{ margin: 0 }}>Op afdrukken en in het projectrapport staat onderaan <i>powered by bsmartr</i>. Het inlogscherm blijft in de bsmartr-stijl.</p>
        </div>
      </div>
    </div>
  );
}
